import { Button, Checkbox, Stack, Typography } from '@vapor/material'
import { GridColDef, GridColumnVisibilityModel, GridSortModel } from '@mui/x-data-grid'
import { keysOf } from 'src/utils/utils'
import { useTranslation } from 'react-i18next'
import {
  reservationSearchFragment,
  ReservationSearchQuery,
  ReservationSearchQueryVariables,
  ReservationSearchDocument,
  ReservationFullTextQueryFilter,
  useReservationStatesQuery,
  ReservationFullTextQuerySortInput,
  ReservationFullTextQueryDateRangeType,
  ReservationStateFragment,
  useOriginsQuery,
  useUsedPortalQuery,
  OriginFragment,
  UsedPortalFragment,
  usePortalQuery,
} from 'src/api/api.generated'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import { RichGraphQLDataGrid } from 'hic-components'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useState } from 'react'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { Select, MenuItem, ListItemText } from '@mui/material'
import { isValid } from 'date-fns'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import HicDateRangePicker from 'src/app/components/hic-date-range-picker/hic-date-range-picker'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { OperationIfGranted } from 'src/app/components/authorization/operation-if-granted'
import { HicOperations, hicOperationsPermissions } from 'src/app/components/authorization/pages-permissions'
import GuestCell from 'src/app/components/guest-cell/guest-cell'
import { HicChip } from 'src/app/components/hic-chip/hic-chip'
import { PortalAvatar } from '../../settings/channel/components/portal-avatar'
import { compareDates } from 'src/utils/date'
import InputDebounced from 'src/app/components/input-debounced/input-debounced'
import { DateRange } from '@mui/x-date-pickers-pro'
import { useHourlyResourceTypeCheck } from 'src/app/hooks/hourly-resource-type/hourly-resource-type'
import { EditAction } from 'src/app/components/data-grid-action-cell-item'
import { useHicCommonDataGridProps } from 'src/app/components/hic-vapor/datagrid/datagrid'

export type ReservationListProp = {
  propertyId: string
  onEditClick: (reservationId: string) => void
  onAddSingleReservationClick: () => void
  onAddGroupReservationClick: () => void
  onAddTimeReservationClick: () => void
}

function ReservationList({ propertyId, onEditClick, onAddSingleReservationClick, onAddTimeReservationClick }: ReservationListProp) {
  const tenant = useTenant()
  const { t } = useTranslation()
  const { sm } = useBreakpoints()
  const formatDate = useFormatDate()
  const hourlyResourceTypeExists = useHourlyResourceTypeCheck(tenant.id, propertyId)
  const { hicCommonDataGridPremiumProps } = useHicCommonDataGridProps()

  const [reservationStatFilterId, setReservationStatFilterId] = useState<string | undefined>()
  const [reservationStateConfirmedId, setReservationStateConfirmedId] = useState<string | undefined>()
  const [reservationStateOptioned, setReservationStateOptioned] = useState<ReservationStateFragment | undefined>()
  const [reservationStates, setReservationStates] = useState<ReservationStateFragment[]>([])
  const { loading: reservationStatesLoading } = useReservationStatesQuery({
    variables: { tenantId: tenant.id },
    onCompleted: (data) => {
      const confirmedState = data.reservationStates?.find((reservationState) => reservationState.confirmed)?.id
      const optionedState = data.reservationStates?.find((reservationState) => reservationState.optioned)
      setReservationStates(data.reservationStates ?? [])
      setReservationStateConfirmedId(confirmedState)
      setReservationStatFilterId(confirmedState)
      setReservationStateOptioned(optionedState)
    },
  })
  const { data: originsData } = useOriginsQuery({
    variables: {
      tenantId: tenant.id,
      pms: true,
    },
  })

  const { data: portalsData } = usePortalQuery({})

  const { data: usedPortalsData } = useUsedPortalQuery({
    variables: {
      filter: {
        tenantId: {
          eq: tenant.id,
        },
        propertyId: {
          eq: propertyId,
        },
      },
    },
  })

  const origins = originsData?.origins?.map((origin) => ({ ...origin, commercialName: t(`pms.booking.reservation.filter.origin.${origin.name}`) })) ?? []
  const portals = portalsData?.portals?.map((portal) => portal) ?? []
  const usedPortals = usedPortalsData?.channels?.map((channel) => channel.portal) ?? []

  const showError = useErrorSnackBar()

  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false)

  const hotelDate = useHotelDate()

  const columns = useReservationColumns({ onEditClick, origins, portals, reservationStateOptionedName: reservationStateOptioned?.name ?? '' })

  const reservationFragmentKey = keysOf<reservationSearchFragment>()
  const [sort, setSort] = useState<ReservationFullTextQuerySortInput>()
  const onSortModelChange = (model: GridSortModel) => {
    const updatedSortCriteria: ReservationFullTextQuerySortInput = {}

    model.map((s) => {
      switch (s.field) {
        case reservationFragmentKey('reservationCode'):
          updatedSortCriteria.createdOn = s.sort
          break
        case reservationFragmentKey('mainGuest'):
          updatedSortCriteria.mainGuest = s.sort
          break
        case reservationFragmentKey('start'):
          updatedSortCriteria.start = s.sort
          break
        case reservationFragmentKey('end'):
          updatedSortCriteria.end = s.sort
          break
        case reservationFragmentKey('reservationState'):
          updatedSortCriteria.reservationState = s.sort
          break
      }
    })

    setSort(updatedSortCriteria)
  }

  const variables = useCallback(
    ({ filter, pagination }): ReservationSearchQueryVariables => {
      return {
        ...pagination,
        // limit: 25, // !!!WORKAROUND!!! to fix issue of MUI DataGrid (see: https://github.com/mui/mui-x/issues/4184) TODO: Please fix when the issue will be resolved!
        filter: {
          tenantId: tenant.id,
          propertyId: propertyId,
          filterFrom: hotelDate,
          filterTo: hotelDate,
          filterType: ReservationFullTextQueryDateRangeType.IN_HOUSE,
          reservationStateId: filter?.reservationStateId ?? reservationStateConfirmedId,
          ...filter,
        },
        sort: sort ? { ...sort } : { createdOn: 'desc' },
      }
    },
    [tenant.id, propertyId, hotelDate, sort, reservationStatesLoading, reservationStateConfirmedId],
  )
  
  const mobileColumnsVisibility: GridColumnVisibilityModel = {
    [reservationFragmentKey('reservationCode')]: false,
  }
  const desktopColumnsVisibility: GridColumnVisibilityModel = {
    [reservationFragmentKey('reservationCode')]: true,
  }
  
  const [showCreationPopOver, setShowCreationPopOver] = useState<HTMLButtonElement | null>()
  
  return (
    <>
      <RichGraphQLDataGrid<ReservationSearchQuery, reservationSearchFragment, ReservationSearchQueryVariables, ReservationFullTextQueryFilter>
    {...hicCommonDataGridPremiumProps}
        data={(response) => response?.reservationSearch || []}
        // pagination="infinite-scroll" // FIXME: ReservationSearchQuery issue with infinite-scroll and filters
        pagination="standard"
        pageSize={10}
        columns={columns}
        query={ReservationSearchDocument}
        variables={variables}
        getRowId={(row) => row.id}
        actions={[
          {
            primary: true,
            position: 'right',
            component: (
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={onAddSingleReservationClick} fullWidth={false} startIcon={<FontAwesomeIcon icon={faPlus} />}>
                  {t('commons.add')}
                </Button>
                {hourlyResourceTypeExists && (
                  <Button variant="outlined" onClick={onAddTimeReservationClick} fullWidth={false} startIcon={<FontAwesomeIcon icon={faPlus} />}>
                    {t('commons.add') + ' Oraria'}
                  </Button>
                )}
              </Stack>
            ),
            mobileComponent: (
              <Button variant="contained" onClick={onAddSingleReservationClick} fullWidth={false} size="small">
                {t('commons.add')}
              </Button>
            ),
          },
        ]}
        filters={[
          {
            label: t('commons.search'),
            tooltip: t('pms.booking.reservation.list.filter_search_tooltip'),
            primary: true,
            component: ({ value, onChange }) => {
              return (
                <InputDebounced
                  value={value?.search ?? ''}
                  onChange={(newValue) => {
                    onChange({ ...value, tenantId: tenant.id, propertyId, search: newValue })
                  }}
                />
              )
            },
            reset: (value) => ({ ...value, search: '' }),
            serialization: (v) => v.search ?? '',
          },
          {
            label: t('pms.booking.reservation.reservation_state'),
            primary: true,
            component: ({ value, onChange }) => {
              return (
                <Select
                  value={reservationStatFilterId ?? 'default'}
                  sx={{ width: '150px' }}
                  onChange={(event) => {
                    const selectedState = reservationStates.find((s) => s.id === event.target.value)
                    if (value?.filterType === 'CANCELATION' && selectedState && !selectedState.canceled) {
                      showError(
                        t('pms.booking.reservation.reservation_state_incompatible_with_visualized_filter', {
                          selectedFilter: t('pms.booking.reservation.reservation_state'),
                          currentFilter: t('pms.booking.reservation.list.filter_search_view_dates'),
                        }),
                      )
                    } else {
                      const previousState = reservationStates.find((s) => s.id === value?.reservationStateId)

                      setReservationStatFilterId(selectedState?.id)
                      onChange({
                        ...value,
                        filterType: previousState?.optioned && value?.filterType === ReservationFullTextQueryDateRangeType.EXPIRATION ? 'IN_HOUSE' : value?.filterType,
                        reservationStateId: selectedState?.id,
                        tenantId: tenant.id,
                        propertyId,
                      })
                    }
                  }}
                >
                  <MenuItem value={'default'}>{t('pms.booking.reservation.reservation_state.all')}</MenuItem>

                  {reservationStates.map((state) => (
                    <MenuItem value={state.id} key={state.id}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
              )
            },
            reset: (value) => ({ ...value, reservationStateId: reservationStateConfirmedId }),
            serialization: (v) => reservationStates?.find((reservationState) => v.reservationStateId === reservationState.id)?.name ?? '',
          },
          {
            label: t('pms.booking.reservation.list.filter_search_view_dates'),
            tooltip: t('pms.booking.reservation.list.filter_search_view_dates_tooltip'),
            primary: true,
            reset: (value) => ({ ...value, filterFrom: undefined, filterTo: undefined, filterType: ReservationFullTextQueryDateRangeType.IN_HOUSE }),
            serialization: (v) => (v.filterType ? `${v.filterType}` : undefined),
            component: ({ onChange, value }) => (
              <Select
                sx={{ width: '150px' }}
                value={value?.filterType ?? ReservationFullTextQueryDateRangeType.IN_HOUSE}
                onChange={(event) => {
                  const fromDate = value?.filterFrom || hotelDate
                  const toDate = value?.filterTo || hotelDate
                  const filterType = event.target.value as ReservationFullTextQueryDateRangeType
                  const reservationStateId = filterType === 'CANCELATION' ? undefined : value?.reservationStateId ?? undefined
                  setReservationStatFilterId(reservationStateId)
                  onChange({
                    ...value,
                    tenantId: tenant.id,
                    propertyId,
                    filterFrom: fromDate,
                    filterTo: toDate,
                    filterType: filterType,
                    reservationStateId: reservationStateId, // Se seleziono CANCELLATE il filtro stato viene resettato
                  })
                }}
              >
                <MenuItem value={ReservationFullTextQueryDateRangeType.ARRIVAL}>{t('pms.booking.reservation.coming')}</MenuItem>
                <MenuItem value={ReservationFullTextQueryDateRangeType.DEPARTURE}>{t('pms.booking.reservation.leaving')}</MenuItem>
                <MenuItem value={ReservationFullTextQueryDateRangeType.CREATION}>{t('pms.booking.reservation.creation')}</MenuItem>
                <MenuItem value={ReservationFullTextQueryDateRangeType.CANCELATION}>{t('pms.booking.reservation.canceled')}</MenuItem>
                <MenuItem value={ReservationFullTextQueryDateRangeType.IN_HOUSE}>{t('pms.booking.reservation.in_house')}</MenuItem>
                <MenuItem value={ReservationFullTextQueryDateRangeType.EXPIRATION} sx={{ display: reservationStatFilterId === reservationStateOptioned?.id ? 'flex' : 'none' }}>
                  {t('pms.booking.reservations_list.expiration')}
                </MenuItem>
              </Select>
            ),
          },
          {
            label: '',
            primary: true,
            component: ({ value, onChange }) => {
              return (
                <HicDateRangePicker
                  value={[value?.filterFrom ?? hotelDate, value?.filterTo ?? hotelDate]}
                  onChange={(date: DateRange<Date | undefined>) => {
                    const filterFrom = isValid(date[0]) ? date[0] : undefined
                    const filterTo = isValid(date[1]) ? date[1] : undefined

                    onChange({
                      ...value,
                      tenantId: tenant.id,
                      propertyId,
                      filterType: value?.filterType ?? ReservationFullTextQueryDateRangeType.IN_HOUSE,
                      filterFrom: filterFrom,
                      filterTo: filterTo,
                    })
                  }}
                  start={{
                    startLabel: t('commons.from_date'),
                    startPlaceholder: t('commons.datepicker.placeholeder'),
                  }}
                  end={{
                    endLabel: t('commons.to_date'),
                    endPlaceholder: t('commons.datepicker.placeholeder'),
                  }}
                  controlled={{
                    open: openDatePicker,
                    setOpen: () => setOpenDatePicker(true),
                    setClose: () => setOpenDatePicker(false),
                  }}
                />
              )
            },
            reset: (value) => ({ ...value, filterFrom: undefined, filterTo: undefined }),
            serialization: (v) => {
              return v.filterFrom && v.filterTo
                ? `${formatDate(v.filterFrom)} ${formatDate(v.filterTo)}`
                : v.filterFrom && v.filterTo
                ? `${formatDate(v.filterFrom)} ${formatDate(v.filterTo)}`
                : undefined
            },
          },
          {
            label: t('pms.booking.reservation.filter.origin'),
            primary: true,
            component: ({ value, onChange }) => {
              return (
                <Select
                  multiple
                  value={[...(value?.originId ?? []), ...(value?.portalId ?? [])]}
                  sx={{ width: '150px' }}
                  onChange={(event) => {
                    const originId: string[] = []
                    const portalId: string[] = []
                    const selectIds = event.target.value as string[]

                    for (const id of selectIds) {
                      if (origins.find((origin) => origin.id === id)) {
                        originId.push(id)
                      } else {
                        portalId.push(id)
                      }
                    }

                    onChange({ ...value, tenantId: tenant.id, propertyId, originId: originId.length ? originId : undefined, portalId: portalId.length ? portalId : undefined })
                  }}
                  renderValue={(selected) => {
                    const originsSelected = origins.filter((origin) => origin.pms && selected.includes(origin.id))
                    const portalsSelected = usedPortals.filter((portal) => selected.includes(portal.id))

                    return `${originsSelected.map((origin) => origin.commercialName).join(', ')}${originsSelected.length && portalsSelected.length ? ', ' : ''}${portalsSelected
                      .map((portal) => portal.commercialName)
                      .join(', ')}`
                  }}
                >
                  {origins
                    .filter((origin) => origin.pms)
                    .map((origin) => (
                      <MenuItem value={origin.id} key={origin.id}>
                        <Checkbox checked={value?.originId?.includes(origin.id) ?? false} />
                        <ListItemText primary={origin.commercialName} />
                      </MenuItem>
                    ))}
                  {usedPortals.map((portals) => (
                    <MenuItem value={portals.id} key={portals.id}>
                      <Checkbox checked={value?.portalId?.includes(portals.id) ?? false} />
                      <ListItemText primary={portals.commercialName} />
                    </MenuItem>
                  ))}
                </Select>
              )
            },
            reset: (value) => ({ ...value, originId: undefined, portalId: undefined }),
            serialization: (v) => {
              const originsSelected = origins.filter((origin) => origin.pms && v.originId?.includes(origin.id))
              const portalsSelected = portals.filter((portal) => v.portalId?.includes(portal.id))

              return `${originsSelected.map((origin) => origin.commercialName).join(', ')}${originsSelected.length && portalsSelected.length ? ' ,' : ''}${portalsSelected
                .map((portal) => portal.commercialName)
                .join(', ')}`
            },
          },
        ]}
        onSortModelChange={onSortModelChange}
        columnVisibilityModel={sm ? desktopColumnsVisibility : mobileColumnsVisibility}
        autoHeight
      />
    </>
  )
}

export default ReservationList

const useReservationColumns = ({
  onEditClick,
  origins,
  portals,
  reservationStateOptionedName,
}: {
  onEditClick: (vatId: string) => void
  origins: (OriginFragment & { commercialName: string })[]
  portals: UsedPortalFragment[]
  reservationStateOptionedName: string
}): GridColDef[] => {
  const { t } = useTranslation()
  const { sm } = useBreakpoints()
  const reservationFragmentKey = keysOf<reservationSearchFragment>()
  const formatDate = useFormatDate()
  const hotelDate = useHotelDate()

  return [
    {
      field: reservationFragmentKey('reservationCode'),
      headerName: t('pms.booking.reservation.code'),
      disableColumnMenu: true,
      sortComparator: (a: string, b: string) => {
        const v1 = a.split('/')
        const v2 = b.split('/')
        if (v1.length === 2 && v2.length === 2) {
          const n1 = +v1[0] + +v1[1]
          const n2 = +v2[0] + +v2[1]
          return n1 - n2
        }
        return 0
      },
    },
    {
      field: reservationFragmentKey('mainGuest'),
      headerName: t('pms.booking.reservation.reference.main_guest'),
      width: 250,
      minWidth: 150,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params) => {
        return (
          <GuestCell
            mainGuestName={params.row.mainGuest}
            onClick={() => {
              onEditClick(params.row.id)
            }}
            groupName={params.row.group}
          />
        )
      },
    },
    {
      field: 'origin',
      headerName: t('pms.booking.reservation.filter.origin'),
      disableColumnMenu: true,
      sortable: false,
      width: 100,
      renderCell: (params) => {
        const reservation = params.row as reservationSearchFragment

        const origin = origins.find((origin) => reservation?.originId === origin.id)
        const portal = portals.find((portal) => reservation?.portalId === portal.id)

        const isFromPms = origin?.pms

        return (
          <HicChip
            text={isFromPms ? origin.commercialName : portal?.commercialName ?? ''}
            image={!isFromPms && portal ? <PortalAvatar portal={portal.name} sx={{ width: 'unset', height: '100%', padding: 0, margin: 0 }} /> : undefined}
            outlined
          />
        )
      },
    },
    {
      field: reservationFragmentKey('start'),
      headerName: t('pms.booking.reservation_new.check_in'),
      disableColumnMenu: true,
      valueFormatter: (params) => (params.value ? formatDate(params.value) : undefined),
    },
    {
      field: reservationFragmentKey('end'),
      headerName: t('pms.booking.reservation_new.check_out'),
      disableColumnMenu: true,
      valueFormatter: (params) => (params.value ? formatDate(params.value) : undefined),
    },
    {
      field: reservationFragmentKey('reservationState'),
      headerName: t('pms.booking.reservation.reservation_state'),
      maxWidth: 125,
      minWidth: sm ? 125 : 30,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params) => {
        const reservation = params.row as reservationSearchFragment
        return (
          <Stack>
            <Typography variant="body">{reservation.reservationState}</Typography>
            {reservationStateOptionedName === reservation.reservationState && reservation.optionExpiration && (
              <>
                <Typography variant="caption">
                  (<span style={{ color: compareDates(hotelDate, reservation.optionExpiration) >= 0 ? 'var(--error)' : undefined }}>{formatDate(reservation.optionExpiration)}</span>)
                </Typography>
              </>
            )}
          </Stack>
        )
      },
    },
    {
      field: reservationFragmentKey('resources'),
      headerName: t('pms.booking.reservation.resources'),
      disableColumnMenu: true,
      sortable: false,
      maxWidth: 125,
      minWidth: sm ? 125 : 30,
      flex: 1,
    },

    {
      field: reservationFragmentKey('createdOn'),
      headerName: t('pms.settings.menu.creation_title'),
      disableColumnMenu: true,
      sortable: false,
      minWidth: 120,
      valueFormatter: (params) => (params.value ? formatDate(params.value) : undefined),
    },
    {
      field: reservationFragmentKey('canceledOn'),
      headerName: t('pms.settings.menu.deletion_title'),
      disableColumnMenu: true,
      sortable: false,
      minWidth: 120,
      valueFormatter: (params) => (params.value ? formatDate(params.value) : undefined),
      renderCell: (params) => {
        return (
          <Typography variant="body" color="var(--MaximumRed)">
            {params.formattedValue}
          </Typography>
        )
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('commons.table_actions'),
      getActions: (params) => [
        <OperationIfGranted {...hicOperationsPermissions.get(HicOperations.CREATE_RESERVATION)} childrenWithoutPermissions={<EditAction disabled={true} onClick={undefined} />}>
          <EditAction onClick={() => onEditClick(params.row.id)} />
        </OperationIfGranted>,
      ],
    },
  ]
}
